import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Popover } from "@mui/material";
import { Image as ImageIcon, Description as DocumentIcon } from "@mui/icons-material";
import { chatService } from "../../services/chatService";
import useUserStore from "../../store/userStore";
import { multimediaService } from "../../services/multimediaService";
import PaymentModal from "../../components/PaymentModal";
import { paymentEvents } from "../../utils/paymentEvents";
import ShipmentDetailsModal from "./chat/ShipmentDetailsModal";
import MobileChatView from "./chat/MobileChatView";
import MobileChatList from "./chat/MobileChatList";
import DesktopChatArea from "./chat/DesktopChatArea";
import { parseChatData, formatTimestamp } from "../../utils/chatHelpers";
import { useStickToBottomScroll } from "./chat/useStickToBottomScroll";
import { useIntersectionLoadMore } from "../../hooks/useIntersectionLoadMore";
import { ChatListLoadingSkeleton, ChatListItemSkeleton } from "../../features/chats/ChatListItemSkeleton";
import ChatSearchField from "../../components/chat/ChatSearchField";
import ChatEmptyState from "../../components/chat/ChatEmptyState";
import PageTitle from "../../components/layout/PageTitle";
import { Button } from "@/components/ui/button";
import { useChatDraft } from "@/hooks/useChatDraft";
import { writeChatDraft } from "@/utils/chatDraftStorage";
import { consumeStartShipmentIntent } from "@/utils/startNewShipment";

import { isOpenPaymentModalEvent, normalizePaymentPayload } from "../../utils/paymentPayload";

const ChatInterfaceNew = ({
  onSendMessage,
  onUiAction,
  messages = [],
  loading = false,
  chatHistory = [],
  selectedChatId,
  onNewChat,
  onSelectChat,
  chatsLoading = false,
  newChatLoading = false,
  messagesLoading = false,
  messagesLoadError = null,
  onRefreshChats,
  onMenuClick,
  hasMoreChats = false,
  chatsLoadingMore = false,
  onLoadMoreChats,
  voiceStatus = "idle",
  voiceMode = null,
  userTranscript = "",
  assistantTranscript = "",
  voiceNoteRecorded = false,
  voiceNoteDraft = "",
  voiceNoteAttachment = null,
  onVoiceNoteDraftConsumed,
  onVoiceNoteEdited,
  voiceActive = false,
  voiceSessionError = null,
  onClearVoiceSessionError,
  onStartVoiceChat,
  onStartVoiceNote,
  onStopVoiceChat,
  onCancelVoice,
  onConfirmVoiceNote,
  voiceMediaStream = null,
}) => {
  const { user } = useUserStore();
  const navigate = useNavigate();
  const { input, setInput, clearDraft } = useChatDraft(selectedChatId);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadPopoverAnchor, setUploadPopoverAnchor] = useState(null);
  const [showChatView, setShowChatView] = useState(false);
  const [kebabMenuAnchor, setKebabMenuAnchor] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [loadingShipment, setLoadingShipment] = useState(false);
  const [chatSearch, setChatSearch] = useState("");

  const messagesScrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const inputRef = useRef(null);
  const chatListRef = useRef(null);
  const chatListScrollRef = useRef(0);
  const isMobile = window.innerWidth <= 768;
  const startShipmentHandled = useRef(false);

  useEffect(() => {
    if (startShipmentHandled.current) return;
    if (!consumeStartShipmentIntent()) return;
    startShipmentHandled.current = true;
    if (isMobile) setShowChatView("new");
    void onNewChat();
  }, [isMobile, onNewChat]);

  const { scrollToBottom } = useStickToBottomScroll(messagesScrollRef);

  const prevMessagesLengthForScrollRef = useRef(messages.length || 0);
  const prevStreamingRef = useRef(false);

  const loadMoreSentinelRef = useIntersectionLoadMore({
    enabled: hasMoreChats && !chatsLoading,
    isLoading: chatsLoadingMore,
    onLoadMore: onLoadMoreChats,
    rootRef: chatListRef,
    observeKey: chatHistory.length,
    resetKey: selectedChatId,
  });

  const messagesLength = messages.length;
  const lastMessage = messages[messagesLength - 1];
  const isLastStreaming = Boolean(lastMessage?.isStreaming);

  useLayoutEffect(() => {
    const prevLen = prevMessagesLengthForScrollRef.current;
    const grew = messagesLength > prevLen;
    prevMessagesLengthForScrollRef.current = messagesLength;

    const streamingEnded = prevStreamingRef.current && !isLastStreaming && messagesLength > 0;

    if (messagesLength > 0 && (grew || streamingEnded)) {
      scrollToBottom(true);
      requestAnimationFrame(() => scrollToBottom(true));
    }

    prevStreamingRef.current = isLastStreaming;
  }, [messagesLength, isLastStreaming, scrollToBottom]);

  const lastStreamTextLen =
    lastMessage?.isStreaming && typeof lastMessage?.text === "string" ? lastMessage.text.length : 0;

  useLayoutEffect(() => {
    if (lastStreamTextLen > 0) {
      scrollToBottom(false);
    }
  }, [lastStreamTextLen, scrollToBottom]);

  useLayoutEffect(() => {
    if (!messagesLoading && messagesLength > 0) {
      requestAnimationFrame(() => scrollToBottom(true));
    }
  }, [messagesLoading, messagesLength, selectedChatId, scrollToBottom]);

  useEffect(() => {
    if (!voiceNoteDraft?.trim()) return;
    setInput(voiceNoteDraft);
    if (selectedChatId) writeChatDraft(selectedChatId, voiceNoteDraft);
  }, [voiceNoteDraft, setInput, selectedChatId]);

  useEffect(() => {
    if (!voiceNoteRecorded || !voiceNoteDraft?.trim()) return;
    setInput(voiceNoteDraft);
  }, [voiceNoteRecorded, voiceNoteDraft, setInput]);

  const handleSend = async () => {
    if (voiceActive && !voiceNoteRecorded) return;
    const messageText = input.trim() || voiceNoteDraft?.trim() || "";
    if (!messageText && !selectedFile) return;
    const outgoing = messageText || "Please analyze this file.";
    let response;
    if (selectedFile) {
      response = await onSendMessage(outgoing, selectedFile);
      setSelectedFile(null);
    } else {
      response = await onSendMessage(
        outgoing,
        null,
        voiceNoteAttachment
          ? { voice_note: voiceNoteAttachment }
          : {},
      );
    }
    checkForPaymentEvent(response);
    clearDraft();
    onVoiceNoteDraftConsumed?.();
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSend();
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const fileObj = multimediaService.createFilePreview(file);
        setSelectedFile(fileObj);
      } catch (error) {
        console.error("File validation error:", error.message);
      }
    }
    setUploadPopoverAnchor(null);
  };

  const handleUploadClick = (event) => {
    setUploadPopoverAnchor(event.currentTarget);
  };

  const handleUploadClose = () => {
    setUploadPopoverAnchor(null);
  };

  const handlePhotoUpload = () => {
    fileInputRef.current?.click();
  };

  const handleDocumentUpload = () => {
    documentInputRef.current?.click();
  };

  const handleKebabClick = (event) => {
    setKebabMenuAnchor(event.currentTarget);
  };

  const handleKebabClose = () => {
    setKebabMenuAnchor(null);
  };

  const handleViewDetails = async () => {
    setDetailsModalOpen(true);
    setKebabMenuAnchor(null);

    if (selectedChatId && !shipmentDetails) {
      setLoadingShipment(true);
      try {
        const response = await chatService.getShipmentDetailsByChatID(selectedChatId);
        if (response?.data) {
          setShipmentDetails(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch shipment details:", error);
      } finally {
        setLoadingShipment(false);
      }
    }
  };

  const removeFile = () => {
    if (selectedFile) {
      multimediaService.cleanupFilePreview(selectedFile);
    }
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (onSendMessage) {
      const response = await onSendMessage(suggestion.title);
      checkForPaymentEvent(response);
    }
  };

  const checkForPaymentEvent = (response) => {
    const event = response?.payment_event ?? response?.data?.payment_event;
    if (isOpenPaymentModalEvent(event)) {
      paymentEvents.openPaymentModal(normalizePaymentPayload(event, user));
    }
  };

  const parsedChatData = parseChatData(
    chatHistory.find((chat) => chat.id === selectedChatId)?.title,
  );

  const showViewDetails =
    parsedChatData.status === "booked" || parsedChatData.status === "quote";

  const sharedModals = (
    <>
      <ShipmentDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setShipmentDetails(null);
        }}
        shipmentDetails={shipmentDetails}
        loadingShipment={loadingShipment}
        chatTitle={parsedChatData.primaryTitle || "New Chat"}
      />
      <PaymentModal />
    </>
  );

  const voiceInputProps = {
    voiceStatus,
    voiceMode,
    userTranscript,
    assistantTranscript,
    voiceNoteRecorded,
    voiceNoteDraft,
    voiceSessionError,
    onClearVoiceSessionError,
    onStartVoiceChat,
    onStartVoiceNote,
    onStopVoiceChat,
    onCancelVoice,
    onConfirmVoiceNote,
    onVoiceNoteEdited,
    voiceActive,
    voiceMediaStream,
    loading: loading || (voiceActive && !voiceNoteRecorded && !voiceSessionError),
  };

  const handleUiActionWrapped = voiceActive
    ? undefined
    : onUiAction;

  if (isMobile) {
    if (showChatView || selectedChatId) {
      const handleMobileBack = () => {
        setShowChatView(false);
        navigate("/chats/new", { replace: true });
      };

      return (
        <>
          <MobileChatView
            selectedChatId={selectedChatId}
            chatHistory={chatHistory}
            messages={messages}
            loading={loading}
            input={input}
            setInput={setInput}
            handleSend={handleSend}
            handleUploadClick={handleUploadClick}
            inputRef={inputRef}
            handleKeyPress={handleKeyPress}
            selectedFile={selectedFile}
            onRemoveFile={removeFile}
            fileInputRef={fileInputRef}
            documentInputRef={documentInputRef}
            handleFileSelect={handleFileSelect}
            handleSuggestionClick={handleSuggestionClick}
            messagesScrollRef={messagesScrollRef}
            setShowChatView={handleMobileBack}
            handleKebabClick={handleKebabClick}
            kebabMenuAnchor={kebabMenuAnchor}
            onKebabClose={handleKebabClose}
            onNewChat={onNewChat}
            newChatLoading={newChatLoading}
            onViewDetails={handleViewDetails}
            showViewDetails={showViewDetails}
            onUiAction={handleUiActionWrapped}
            voiceInputProps={voiceInputProps}
          />
          {sharedModals}
        </>
      );
    }

    const handleMobileNewChat = async () => {
      setShowChatView("new");
      await onNewChat();
    };

    return (
      <>
        <MobileChatList
          onNewChat={handleMobileNewChat}
          newChatLoading={newChatLoading}
          selectedChatId={selectedChatId}
          onSelectChat={onSelectChat}
          setShowChatView={setShowChatView}
          onMenuClick={onMenuClick}
          chatHistory={chatHistory}
          chatsLoading={chatsLoading}
          hasMoreChats={hasMoreChats}
          chatsLoadingMore={chatsLoadingMore}
          onLoadMoreChats={onLoadMoreChats}
        />
        {sharedModals}
      </>
    );
  }

  if (!chatsLoading && chatHistory.length === 0) {
    return (
      <>
        <div className="flex h-screen flex-col bg-white">
          <PageTitle>Chats</PageTitle>
          <ChatEmptyState onAction={onNewChat} loading={newChatLoading} className="flex-1" />
        </div>
        {sharedModals}
      </>
    );
  }

  const groupChats = (chats) => {
    const now = new Date();
    const today = [];
    const older = [];
    chats.forEach((chat) => {
      const d = new Date(chat.lastMessage || chat.timestamp);
      const diffDays = (now - d) / (1000 * 60 * 60 * 24);
      if (diffDays < 1) today.push(chat);
      else older.push(chat);
    });
    return { today, older };
  };

  const filteredChats = chatHistory.filter((chat) => {
    const data = parseChatData(chat.title);
    const q = chatSearch.toLowerCase();
    return !q || (data.primaryTitle || "").toLowerCase().includes(q) || (chat.preview || "").toLowerCase().includes(q);
  });

  const { today: todayChats, older: olderChats } = groupChats(filteredChats);
  const renderChatItem = (chat) => {
    const data = parseChatData(chat.title);
    const isSelected = chat.id === selectedChatId;
    return (
      <div
        key={chat.id}
        onClick={(e) => {
          e.preventDefault();
          chatListScrollRef.current = chatListRef.current?.scrollTop ?? 0;
          onSelectChat(chat.id);
        }}
        className={`px-4 py-3 mb-4 rounded-xl cursor-pointer transition-all border-[1.5px] border-[#E4E7EC] ${
          isSelected ? "bg-[#F0F4FF]" : "bg-white hover:bg-[#F9FAFB]"
        }`}
      >
        <div className="flex justify-between items-start mb-1">
          <h3 className="font-[600] text-[#1D2739] text-[14px] leading-5 truncate flex-1 pr-2">
            {data.primaryTitle || "New Chat"}
          </h3>
          <span className="text-[10px] text-[#737373] whitespace-nowrap">
            {formatTimestamp(chat.lastMessage || chat.timestamp)}
          </span>
        </div>
        <p className="text-[12px] text-[#1D2739] truncate">{chat.preview || "No messages yet"}</p>
      </div>
    );
  };

  return (
    <div className="flex h-screen">
      {/* Left Side - Chat History */}
      <div className=" max-w-[380px] xl:max-w-[450px] w-full bg-[#F9FAFB] border-r border-[#D0D5DD] flex flex-col">
        {/* Header */}
        <PageTitle
          actions={
            <Button
              type="button"
              onClick={newChatLoading ? undefined : onNewChat}
              disabled={newChatLoading}
              data-tour="new-chat-button"
              className="h-8 min-h-8 gap-1.5 rounded-full bg-[#2563EB] px-3 py-1.5 text-[14px] font-medium text-[#FAFAFA] shadow-none hover:bg-[#1C3BC7] active:scale-[0.98] active:bg-[#1C3BC7]"
            >
              {newChatLoading ? (
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <span className="text-[14px] leading-none">+</span>
              )}
              New
            </Button>
          }
        >
          Chats
        </PageTitle>

        {/* Search */}
        <div className="px-4 py-3">
          <ChatSearchField
            size="docs"
            placeholder="Search chats"
            value={chatSearch}
            onChange={(e) => setChatSearch(e.target.value)}
            onClear={() => setChatSearch("")}
            clearable
          />
        </div>

        {/* Chat List */}
        <div className="overflow-y-auto flex-1" ref={chatListRef}>
          {chatsLoading && chatHistory.length === 0 ? (
            <ChatListLoadingSkeleton rows={6} />
          ) : filteredChats.length === 0 ? (
            chatHistory.length === 0 ? (
              <ChatEmptyState onAction={onNewChat} loading={newChatLoading} className="py-12" />
            ) : (
              <p className="text-center text-[13px] text-[#667185] py-12 px-6">
                No chats match your search.
              </p>
            )
          ) : (
            <div className="px-3 py-1">
              {todayChats.length > 0 && (
                <>
                  <p className="text-[12px] font-[600] text-[#344054] tracking-widest px-1 mb-2">TODAY</p>
                  {todayChats.map(renderChatItem)}
                </>
              )}
              {olderChats.length > 0 && (
                <>
                  <p className="text-[12px] font-[600] text-[#344054] tracking-widest px-1 mt-4 mb-2">OLDER</p>
                  {olderChats.map(renderChatItem)}
                </>
              )}
              {hasMoreChats ? <div ref={loadMoreSentinelRef} className="h-px" aria-hidden /> : null}
              {chatsLoadingMore ? (
                <>
                  <ChatListItemSkeleton />
                  <ChatListItemSkeleton />
                </>
              ) : null}
              {!hasMoreChats && filteredChats.length >= 6 && !chatsLoadingMore ? (
                <p className="text-center text-[11px] text-[#98A2B3] py-3">You&apos;ve reached the end</p>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Chat Area */}
      <DesktopChatArea
        data-tour="conversation-area"
        selectedChatId={selectedChatId}
        chatHistory={chatHistory}
        messages={messages}
        loading={loading}
        messagesLoading={messagesLoading}
        messagesLoadError={messagesLoadError}
        user={user}
        input={input}
        setInput={setInput}
        handleSend={handleSend}
        handleUploadClick={handleUploadClick}
        inputRef={inputRef}
        handleKeyPress={handleKeyPress}
        selectedFile={selectedFile}
        onRemoveFile={removeFile}
        fileInputRef={fileInputRef}
        documentInputRef={documentInputRef}
        handleFileSelect={handleFileSelect}
        handleSuggestionClick={handleSuggestionClick}
        messagesScrollRef={messagesScrollRef}
        parsedChatData={parsedChatData}
        onUiAction={handleUiActionWrapped}
        voiceInputProps={voiceInputProps}
      />

      {/* Upload Popover */}
      <Popover
        open={Boolean(uploadPopoverAnchor)}
        anchorEl={uploadPopoverAnchor}
        onClose={handleUploadClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "& .MuiPaper-root": {
            padding: "8px",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            minWidth: "200px",
            border: "1px solid #E4E7EC",
          },
        }}
      >
        <div
          onClick={handlePhotoUpload}
          className="flex gap-3 items-center px-3 py-3 mb-1 rounded-lg transition-colors cursor-pointer hover:bg-gray-50"
        >
          <ImageIcon sx={{ fontSize: 20, color: "#475467" }} />
          <span className="text-[14px] text-[#344054] font-medium">Upload Photos</span>
        </div>
        <div
          onClick={handleDocumentUpload}
          className="flex gap-3 items-center px-3 py-3 rounded-lg transition-colors cursor-pointer hover:bg-gray-50"
        >
          <DocumentIcon sx={{ fontSize: 20, color: "#475467" }} />
          <span className="text-[14px] text-[#344054] font-medium">Upload Documents</span>
        </div>
      </Popover>

      <ShipmentDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setShipmentDetails(null);
        }}
        shipmentDetails={shipmentDetails}
        loadingShipment={loadingShipment}
        chatTitle={parsedChatData.primaryTitle || "New Chat"}
      />

      <PaymentModal />
    </div>
  );
};

export default ChatInterfaceNew;
